import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { NgbDatepickerConfig, NgbInputDatepickerConfig, NgbPopoverConfig, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { environment } from './environments/environment';
import { appConfig, configureDatepicker } from '@main/app.config';
import { AppComponent } from '@main/app.component';

if (environment.production) {
  enableProdMode();
//   window.console.log = () => { }
}

bootstrapApplication(AppComponent, appConfig)
    .then(appRef => {
        // Retrieve configuration services
        const datepickerConfig = appRef.injector.get(NgbDatepickerConfig);
        const inputDatepickerConfig = appRef.injector.get(NgbInputDatepickerConfig);
        const popoverConfig = appRef.injector.get(NgbPopoverConfig);
        const tooltipConfig = appRef.injector.get(NgbTooltipConfig);

        // Apply configuration settings
        configureDatepicker(datepickerConfig, inputDatepickerConfig, popoverConfig, tooltipConfig);

        // Enable Grammarly on all editable fields in the application
        enableGrammarlyOnAllEditableFields();
    })
    .catch(err => console.error(err));

function enableGrammarlyOnAllEditableFields(): void {
    // Constants
    const SKIP_INPUT_TYPES = ['checkbox', 'radio', 'file', 'button', 'submit', 'reset', 'image', 'hidden', 'range', 'color', 'date', 'datetime-local', 'month', 'time', 'week'];
    const QUILL_CLASSES = ['ql-editor', 'ql-container'];
    const EDITABLE_SELECTOR = 'input:not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="image"]):not([type="hidden"]), textarea, [contenteditable="true"], [contenteditable=""]';

    // Helper function to add Grammarly attributes to an element
    const addGrammarlyAttributes = (element: HTMLElement) => {
        if (element.hasAttribute('data-grammarly-enabled')) {
            return;
        }

        // Add attributes to enable Grammarly
        element.setAttribute('data-gramm_editor', 'true');
        element.setAttribute('data-gramm', 'true');
        element.setAttribute('spellcheck', 'true');
        element.setAttribute('data-grammarly-enabled', 'true');
    };

    // Helper function to check if element is a Quill editor
    const isQuillElement = (element: HTMLElement): boolean => {
        return QUILL_CLASSES.some(className => element.classList.contains(className));
    };

    // Helper function to check if input is text-based
    const isTextBasedInput = (input: HTMLInputElement): boolean => {
        const inputType = input.type?.toLowerCase() || 'text';
        return !SKIP_INPUT_TYPES.includes(inputType);
    };

    // Helper function to enable Grammarly on container if it has editable content
    const enableGrammarlyOnContainer = (container: HTMLElement) => {
        if (!container.hasAttribute('data-grammarly-enabled') &&
            !isQuillElement(container) &&
            container.querySelector(EDITABLE_SELECTOR) !== null) {
            addGrammarlyAttributes(container);
        }
    };

    // Function to enable Grammarly on all editable elements
    const enableGrammarly = () => {

        //Quill editor contentEditable divs
        document.querySelectorAll('.ql-editor[contenteditable="true"], .ql-editor[contenteditable=""], div[contenteditable="true"].ql-editor')
            .forEach((editor: Element) => addGrammarlyAttributes(editor as HTMLElement));

        // 2. Quill container divs as a fallback
        document.querySelectorAll('.ql-container').forEach((container: Element) => {
            const element = container as HTMLElement;
            if (!element.hasAttribute('data-gramm_editor')) {
                element.setAttribute('data-gramm_editor', 'true');
            }
        });

        //All contentEditable elements
        document.querySelectorAll('[contenteditable="true"], [contenteditable=""]').forEach((element: Element) => {
            const htmlElement = element as HTMLElement;
            if (!isQuillElement(htmlElement)) {
                addGrammarlyAttributes(htmlElement);
            }
        });

        //All text-based input fields
        document.querySelectorAll('input').forEach((input: Element) => {
            const htmlInput = input as HTMLInputElement;
            if (isTextBasedInput(htmlInput)) {
                addGrammarlyAttributes(htmlInput);
            }
        });

        //All textarea elements
        document.querySelectorAll('textarea').forEach((textarea: Element) => {
            addGrammarlyAttributes(textarea as HTMLElement);
        });

        //Card elements that are contentEditable
        document.querySelectorAll('.card-body[contenteditable="true"], .card-body[contenteditable=""], .card-text[contenteditable="true"], .card-text[contenteditable=""], .card-title[contenteditable="true"], .card-title[contenteditable=""]')
            .forEach((cardElement: Element) => {
                const htmlElement = cardElement as HTMLElement;
                if (!isQuillElement(htmlElement)) {
                    addGrammarlyAttributes(htmlElement);
                }
            });

        //Input and textarea elements within card elements
        document.querySelectorAll('.card-body, .card-text, .card-title').forEach((cardBody: Element) => {
            const htmlElement = cardBody as HTMLElement;

            // Process inputs and textareas within cards
            htmlElement.querySelectorAll('input, textarea').forEach((input: Element) => {
                const htmlInput = input as HTMLInputElement | HTMLTextAreaElement;
                if (input.tagName.toLowerCase() === 'input') {
                    if (isTextBasedInput(htmlInput as HTMLInputElement)) {
                        addGrammarlyAttributes(htmlInput as HTMLElement);
                    }
                } else {
                    addGrammarlyAttributes(htmlInput as HTMLElement);
                }
            });

            // Enable Grammarly on card element if it contains editable content
            enableGrammarlyOnContainer(htmlElement);
        });

        //P tags that are contentEditable
        document.querySelectorAll('p[contenteditable="true"], p[contenteditable=""]')
            .forEach((pTag: Element) => {
                const htmlElement = pTag as HTMLElement;
                if (!isQuillElement(htmlElement)) {
                    addGrammarlyAttributes(htmlElement);
                }
            });

        //div elements that are contentEditable (excluding Quill editors)
        document.querySelectorAll('div[contenteditable="true"], div[contenteditable=""]')
            .forEach((divElement: Element) => {
                const htmlElement = divElement as HTMLElement;
                if (!isQuillElement(htmlElement) && !htmlElement.hasAttribute('data-grammarly-enabled')) {
                    addGrammarlyAttributes(htmlElement);
                }
            });

        // P and div tags elements that contain editable content
        document.querySelectorAll('p, div').forEach((element: Element) => {
            const htmlElement = element as HTMLElement;
            if (!isQuillElement(htmlElement)) {
                enableGrammarlyOnContainer(htmlElement);
            }
        });

        // Input containers (form groups, input groups, etc.)
        document.querySelectorAll('.input-group, .form-group, .form-control-wrapper, [class*="input"], [class*="form"]')
            .forEach((container: Element) => {
                enableGrammarlyOnContainer(container as HTMLElement);
            });
    };

    // Run immediately
    enableGrammarly();

    const observer = new MutationObserver(() => {
        enableGrammarly();
    });

    // Observe the document body for changes
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false
    });

    setInterval(enableGrammarly, 2000);
}
